/** Task type selector with in-dropdown search (name or catalogue key). */

import { useCallback, useMemo, useState } from "react";
import FormInput from "@/components/ui/form-input";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";

function filterTaskTypeOptions<T extends string>(
  options: SelectOption<T>[],
  search: string,
  selectedValue: T,
): SelectOption<T>[] {
  const needle = search.trim().toLowerCase();
  const filtered = !needle
    ? options
    : options.filter(
        (option) =>
          option.label.toLowerCase().includes(needle) ||
          String(option.value).toLowerCase().includes(needle),
      );
  if (filtered.some((option) => option.value === selectedValue)) {
    return filtered;
  }
  const selected = options.find((option) => option.value === selectedValue);
  return selected ? [selected, ...filtered] : filtered;
}

function stopListboxEvent(event: React.SyntheticEvent) {
  event.stopPropagation();
}

export interface SearchableTaskTypeDropdownProps {
  options: SelectOption<string>[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  buttonClassName?: string;
  inline?: boolean;
  disabled?: boolean;
  "data-testid"?: string;
}

export function SearchableTaskTypeDropdown({
  options,
  value,
  onChange,
  label = "Task type",
  buttonClassName,
  inline,
  disabled,
  "data-testid": dataTestId,
}: SearchableTaskTypeDropdownProps) {
  const [search, setSearch] = useState("");

  const visibleOptions = useMemo(
    () => filterTaskTypeOptions(options, search, value),
    [options, search, value],
  );

  const handleChange = useCallback(
    (nextValue: string) => {
      onChange(nextValue);
      setSearch("");
    },
    [onChange],
  );

  return (
    <SelectDropdown<string>
      inline={inline}
      label={label}
      buttonClassName={buttonClassName}
      options={visibleOptions}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      usePortal
      data-testid={dataTestId}
      renderListHeader={() => (
        <div
          className="sticky top-0 z-10 border-b border-slate-200 bg-white p-2 dark:border-slate-600/60 dark:bg-[#283346]"
          onMouseDown={stopListboxEvent}
          onClick={stopListboxEvent}
        >
          <FormInput
            type="search"
            placeholder="Search task types"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={stopListboxEvent}
            autoFocus
            canClear
            showClearButton
            onClear={() => setSearch("")}
            aria-label="Search task types"
            data-testid={dataTestId ? `${dataTestId}-search` : "task-type-search"}
          />
          {search.trim() && visibleOptions.length === 0 ? (
            <p className="mt-2 px-1 text-xs text-slate-500 dark:text-slate-400">No matching task types.</p>
          ) : null}
        </div>
      )}
    />
  );
}
